<script setup lang="ts">
import { api } from '@/api';
import {
  EvaluationParameterDto,
  EvaluationParameterScopeDto,
  ParameterCombinationDto,
  TrackItMeasurementFilterForCombinationDto,
  TrackItMeasurementInfoDto,
  TrackItTaskResultForEvaluation,
  TrackItTaskWorkResultDto,
  TrackItTaskWorkStepDto,
} from '@/api/generator';
import Button from '@/components/buttons/Button.vue';
import CommentInlineDialog from '@/components/comments/CommentInlineDialog.vue';
import Checkbox from '@/components/forms/Checkbox/Checkbox.vue';
import Dialog from '@/components/Dialog/Dialog.vue';
import Notification from '@/components/notifications/Notification.vue';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import DateTimeFormatter from '@/components/formatters/DateTimeFormatter.vue';
import { formatTimePeriod } from '@/components/formatters/utils';
import { fullName } from '@/utils/helper';
import { I18nKey } from '@/utils/typings';
import { mdiPlus, mdiRadioboxBlank, mdiRadioboxMarked } from '@mdi/js';
import TimeStamp from '@/components/EntityHistory/components/TimeStamp.vue';
import { uniqBy } from 'lodash';
import { NIL, v7 as uuidv7 } from 'uuid';
import { computed, ref, watch } from 'vue';
import { useTranslation } from 'i18next-vue';
import {
  describeTrackItFilterWithValue,
  isParameterFilterEntry,
  trackItFilterIdentityKey,
  trackItFilterName,
  translateMeasurementPropertyName,
} from '@/views/tasks/components/trackItFilters';
import ImportedMeasurementOverviewFilter from '@/views/importedMeasurements/components/ImportedMeasurementOverviewFilter.vue';
import { useTrackItMeasurements } from '@/views/importedMeasurements/useTrackItMeasurements';
import { useJobOccurrence } from '../../utils/useJobOccurrence';
import {
  buildTrackItTaskResultForEvaluation,
  TrackItManualValue,
} from '../../utils/trackItTaskResultForEvaluation';
import { useTrackItWorkStepPreview } from '../../utils/useTrackItWorkStepPreview';
import TrackItDataPointsTable from './TrackItDataPointsTable.vue';
import TrackItMeasurementParametersTable from './TrackItMeasurementParametersTable.vue';
import TrackItMeasurementResultPreview from './TrackItMeasurementResultPreview.vue';

const props = withDefaults(
  defineProps<{
    // The work step to import into. Without one (from the task configuration) the dialog previews
    // a measurement against `combination` and `evaluationScopes` instead: no job time frame,
    // no manual values, nothing to apply.
    workStep?: TrackItTaskWorkStepDto;
    // Defaults to the work step's combination.
    combination?: ParameterCombinationDto;
    // The task area's evaluation scopes, for the preview without a work step.
    evaluationScopes?: EvaluationParameterScopeDto[];
    // Measurements already picked elsewhere (e.g. for other work steps of the same import), so
    // they aren't offered again.
    measurementIdsToExclude?: number[];
    // Defaults to the job's medical system's alias.
    trackItAlias?: string | null;
    skipPreview?: boolean;
  }>(),
  {
    workStep: undefined,
    combination: undefined,
    evaluationScopes: undefined,
    measurementIdsToExclude: undefined,
    trackItAlias: undefined,
    skipPreview: false,
  },
);

const combination = computed(() => props.combination ?? props.workStep?.combination);
const canImport = computed(() => !!props.workStep);

const emits = defineEmits<{
  apply: [
    payload: {
      trackItMeasurementId: number;
      additionalResults: TrackItTaskResultForEvaluation[];
      result: TrackItTaskWorkResultDto;
    },
  ];
}>();

const modelValue = defineModel<boolean>({ required: true });

const { t } = useTranslation();

const { jobOccurrence } = useJobOccurrence();

// Shown like the job overview's "Period of execution", so the user sees what is filtered by.
const jobTimeFrame = computed(() =>
  formatTimePeriod(
    jobOccurrence.value.start,
    jobOccurrence.value.end,
    !jobOccurrence.value.executionAllDay,
  ),
);

const trackItAlias = computed(() => {
  if (props.trackItAlias) return props.trackItAlias;
  const targetSystem = canImport.value ? jobOccurrence.value.targetSystem : undefined;
  return targetSystem && 'trackItAlias' in targetSystem ? targetSystem.trackItAlias : undefined;
});
const { fetchPreview: fetchWorkStepPreview } = useTrackItWorkStepPreview();

type Step = 'select' | 'preview';

const step = ref<Step>('select');

const previewLoading = ref(false);
const selectedId = ref<number | undefined>(undefined);
const previewResult = ref<TrackItTaskWorkResultDto | undefined>(undefined);
const additionalResults = ref<TrackItTaskResultForEvaluation[]>([]);
const filterJobTimeFrame = ref(true);

// The Track-it filters of the combination; ignored (disabled) ones don't filter.
const trackItFilterSections = computed(() => {
  const chips = (filters: TrackItMeasurementFilterForCombinationDto[], bold: boolean) =>
    filters.map((filter) => {
      const rawName = trackItFilterName(filter) ?? '';
      const name = bold ? rawName : translateMeasurementPropertyName(rawName);
      return {
        key: trackItFilterIdentityKey(filter),
        name,
        rest: describeTrackItFilterWithValue(filter).slice(name.length),
        bold,
      };
    });
  const enabledFilters = (combination.value?.trackItFilters ?? []).filter(
    (filter) => filter.isEnabled,
  );

  return [
    {
      titleKey: 'TrackItMeasurementParameter_other',
      chips: chips(enabledFilters.filter(isParameterFilterEntry), true),
    },
    {
      titleKey: 'TrackItMeasuringProperty_other',
      chips: chips(
        enabledFilters.filter((filter) => !isParameterFilterEntry(filter)),
        false,
      ),
    },
  ].filter((section) => section.chips.length);
});

type Header = { key: string; title: string; sortable?: boolean; width?: number };

const headers: Header[] = [
  { key: 'select', title: '', sortable: false, width: 48 },
  { key: 'measurementDate', title: t('MeasurementDate') },
  { key: 'radiationUnit', title: t('TrackItAlias') },
  { key: 'measurementDevice', title: t('TrackItMeasuringDevice') },
  { key: 'measurementSoftware', title: t('TrackItMeasuringSoftware') },
  { key: 'importDate', title: t('Import') },
  { key: 'comments', title: '', sortable: false },
];

const expandedMeasurements = ref<string[]>([]);

const noMeasurementsMessage = computed(() =>
  canImport.value && filterJobTimeFrame.value && !filter.value.search
    ? t('TrackItNoMeasurementsInJobTimeFrame' as I18nKey)
    : t('TrackItNoMatchingMeasurements' as I18nKey),
);

const {
  defaultOptions,
  filter,
  measurements,
  itemsLength,
  loading,
  loadMeasurements,
  updateOptions,
} = useTrackItMeasurements(async (query) => {
  if (!combination.value) return undefined;

  // Without "consider job time frame" (or without a job at all) the search isn't limited in time.
  const limitToJobTimeFrame = canImport.value && filterJobTimeFrame.value;

  return api.trackItImportApi.getFilteredTrackItMeasurements({
    ...query,
    getTrackItMeasurementsDto: {
      combination: combination.value,
      targetSystemTrackItAlias: trackItAlias.value,
      startTime: limitToJobTimeFrame ? jobOccurrence.value.start : undefined,
      endTime: limitToJobTimeFrame ? jobOccurrence.value.end : undefined,
      measurementIdsToExclude: canImport.value ? props.measurementIdsToExclude : undefined,
    },
  });
});

watch(filterJobTimeFrame, () => loadMeasurements());

function selectRow(item: TrackItMeasurementInfoDto) {
  selectedId.value = item.id;
  additionalResults.value = [];
}

function close() {
  modelValue.value = false;
}

function back() {
  step.value = 'select';
  previewResult.value = undefined;
}

function emitApply() {
  if (selectedId.value === undefined || !previewResult.value) {
    return;
  }

  emits('apply', {
    trackItMeasurementId: selectedId.value,
    additionalResults: additionalResults.value,
    result: previewResult.value,
  });

  close();
}

async function fetchPreview(trackItMeasurementId: number) {
  if (props.workStep) {
    return fetchWorkStepPreview(trackItMeasurementId, props.workStep.id, additionalResults.value);
  }
  if (!combination.value) return undefined;

  previewScopes.value = withPreviewIds(props.evaluationScopes ?? []);
  const { result, success } = await api.trackItImportApi.getTrackItExecutionPreviewForTaskArea({
    importTrackItMeasurementForTaskAreaDto: {
      trackItMeasurementId,
      combination: combination.value,
      evaluationParameterScopes: previewScopes.value,
      additionalResults: [],
    },
  });
  return success ? result : undefined;
}

// Unsaved evaluation parameters still carry the empty id, and the backend would give them a
// random one for the preview — its results would then refer to an id unknown here. Sending them
// with an id of our own keeps the results matchable to the evaluations.
const previewScopes = ref<EvaluationParameterScopeDto[]>([]);

function withPreviewIds(scopes: EvaluationParameterScopeDto[]): EvaluationParameterScopeDto[] {
  return scopes.map((scope) => ({
    ...scope,
    evaluationParameters: scope.evaluationParameters?.map((evaluation) =>
      !evaluation.id || evaluation.id === NIL ? { ...evaluation, id: uuidv7() } : evaluation,
    ),
  }));
}

// The work step knows its evaluations; without one, they're the scopes' evaluations the preview
// covers — those it evaluated plus those it couldn't map to a Track-it data type.
const previewEvaluations = computed(() => {
  if (props.workStep) return props.workStep.evaluations ?? undefined;

  const coveredIds = new Set([
    ...(previewResult.value?.evaluationResults ?? []).map((result) => result.evaluationParameterId),
    ...(previewResult.value?.unmappedEvaluationIds ?? []),
  ]);
  return uniqBy(
    previewScopes.value.flatMap((scope) => scope.evaluationParameters ?? []),
    'id',
  ).filter((evaluation) => coveredIds.has(evaluation.id));
});

async function showPreview() {
  if (selectedId.value === undefined) {
    return;
  }

  previewLoading.value = true;
  const result = await fetchPreview(selectedId.value);
  previewLoading.value = false;

  if (!result) {
    return;
  }

  previewResult.value = result;

  if (props.skipPreview) {
    emitApply();
  } else {
    step.value = 'preview';
  }
}

async function onManualValue(evaluation: EvaluationParameterDto, value: TrackItManualValue | null) {
  if (selectedId.value === undefined || !props.workStep) {
    return;
  }

  const withoutEvaluation = additionalResults.value.filter(
    (additionalResult) => additionalResult.evaluationParameterId !== evaluation.id,
  );
  // An emptied field means "no manual value for this evaluation" — nothing to persist yet in
  // this pre-import preview, so it's dropped rather than kept as an explicit null entry.
  additionalResults.value =
    value === null
      ? withoutEvaluation
      : [...withoutEvaluation, buildTrackItTaskResultForEvaluation(evaluation, value)];

  previewLoading.value = true;
  const result = await fetchPreview(selectedId.value);
  previewLoading.value = false;

  if (result) {
    previewResult.value = result;
  }
}
</script>

<template>
  <Dialog
    :modelValue="modelValue"
    size="x-large"
    @update:modelValue="(value) => (modelValue = value)"
  >
    <template #title>
      <h3>
        {{
          step === 'preview'
            ? $t('Preview')
            : canImport
              ? $t('SelectTrackItMeasurement')
              : $t('TrackItMatchingMeasurements')
        }}
      </h3>
    </template>

    <template #text>
      <template v-if="step === 'select'">
        <div v-if="canImport" class="mb-4">
          <Checkbox
            v-model="filterJobTimeFrame"
            hideDetails
            :label="$t('JobOccurrencePeriodOfExecution')"
            class="mb-1"
          />
          <v-chip class="text-body-2" :disabled="!filterJobTimeFrame">{{ jobTimeFrame }}</v-chip>
        </div>

        <div v-if="trackItAlias" class="mb-4">
          <v-label class="mb-1 d-block">
            {{ $t('TrackItAlias' as I18nKey) }}
          </v-label>
          <v-chip class="text-body-2">{{ trackItAlias }}</v-chip>
        </div>

        <Notification
          v-if="!trackItFilterSections.length"
          :value="{
            type: 'info',
            message: $t('TrackItNoFiltersMessage' as I18nKey),
            closable: false,
          }"
          class="mb-4"
        />
        <v-row v-else class="mb-2">
          <v-col v-for="section in trackItFilterSections" :key="section.titleKey">
            <h6>{{ $t(section.titleKey as I18nKey) }}</h6>
            <div class="d-flex flex-wrap ga-2">
              <v-chip
                v-for="chip in section.chips"
                :key="chip.key"
                label
                size="small"
                variant="tonal"
              >
                <span
                  ><span :class="{ 'font-weight-bold': chip.bold }">{{ chip.name }}</span
                  >{{ chip.rest }}</span
                >
              </v-chip>
            </div>
          </v-col>
        </v-row>

        <ImportedMeasurementOverviewFilter v-model="filter" class="mb-2" />

        <v-card elevation="1">
          <OverviewTable
            v-model:expanded="expandedMeasurements"
            :headers="headers"
            :noDataLabel="noMeasurementsMessage"
            :items="measurements"
            :itemsLength="itemsLength"
            :loading="loading"
            itemValue="id"
            showExpand
            :serverSidePagination="true"
            :hideDefaultFooter="false"
            :showItemsCount="false"
            :itemsPerPage="defaultOptions.itemsPerPage"
            :defaultSortBy="defaultOptions.sortBy"
            @update:options="updateOptions"
            @click:row="
              (_event: unknown, { item }: { item: TrackItMeasurementInfoDto }) => selectRow(item)
            "
          >
            <template #item.select="{ item }">
              <v-icon
                :icon="selectedId === item.id ? mdiRadioboxMarked : mdiRadioboxBlank"
                color="primary"
                @click.stop="selectRow(item)"
              />
            </template>

            <template #item.measurementDate="{ item }">
              <DateTimeFormatter :value="item.measurementDate" />
            </template>

            <template #item.importDate="{ item }">
              <TimeStamp
                :time="item.importDate"
                :user="fullName(item.importUser ?? undefined)"
                :icon="mdiPlus"
              />
            </template>

            <template #item.comments="{ item }">
              <span @click.stop>
                <CommentInlineDialog
                  :entity="item"
                  entityWithCommentType="WorkResult"
                  readonly
                  :showCountAlways="false"
                />
              </span>
            </template>

            <template #expanded-row="{ columns, item }">
              <tr>
                <td :colspan="columns.length" class="pa-4">
                  <v-row>
                    <v-col v-if="item.measurementParameters?.length" cols="6">
                      <TrackItMeasurementParametersTable
                        :title="$t('Parameter_other')"
                        :parameters="item.measurementParameters"
                      />
                    </v-col>

                    <v-col v-if="item.dataTypes?.length" cols="6">
                      <TrackItDataPointsTable
                        title="Track-It Data Points"
                        :dataPoints="item.dataTypes"
                      />
                    </v-col>
                  </v-row>
                </td>
              </tr>
            </template>
          </OverviewTable>
        </v-card>
      </template>

      <TrackItMeasurementResultPreview
        v-else-if="previewResult"
        :result="previewResult"
        :evaluations="previewEvaluations"
        :loading="previewLoading"
        :manualEntryEnabled="canImport"
        @manualValue="onManualValue"
      />
    </template>

    <template #actions>
      <template v-if="step === 'select'">
        <Button variant="text" :label="$t('Cancel')" @click="close" />
        <Button
          variant="secondary"
          :disabled="selectedId === undefined"
          :label="skipPreview ? $t('Apply') : $t('Preview')"
          :loading="previewLoading"
          @click="showPreview"
        />
      </template>

      <template v-else>
        <Button variant="text" :label="$t('Back')" @click="back" />
        <Button variant="text" :label="canImport ? $t('Cancel') : $t('Close')" @click="close" />
        <Button v-if="canImport" variant="secondary" :label="$t('Apply')" @click="emitApply" />
      </template>
    </template>
  </Dialog>
</template>
